import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { MonoLabel } from '../components/Editorial';
import SchoolPicker from '../components/SchoolPicker';
import SignupSchoolSection from '../components/SignupSchoolSection';
import { EMPTY_COLLEGE, collegeComplete, collegePayload } from '../utils/signupCollege';
import { useSchoolData } from '../hooks/useSchoolData';
import { registrationErrorMessage } from '../lib/apiError';
import { getAttribution, clearAttribution } from '../lib/attribution';
import { HEARD_ABOUT_OPTIONS } from '../constants/heardAboutOptions';
import Button from '../components/Button';

const SPECIAL = /[!@#$%^&*(),.?":{}|<>]/;
const PW_RULES = [
  { key: 'length',    label: 'At least 8 characters',           test: (p) => p.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter',            test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'One lowercase letter',            test: (p) => /[a-z]/.test(p) },
  { key: 'special',   label: 'One special character (!@#$%^&*)', test: (p) => SPECIAL.test(p) },
];

function formatPhone(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function isValidPhone(formatted) {
  return (formatted || '').replace(/\D/g, '').length === 10;
}

/**
 * Parent-creates-athlete signup. Mirrors iOS ParentRegistrationView: collects
 * parent account fields and child athlete fields in one form, and posts with
 * registration_type='parent_creates_athlete' so the backend spins up both a
 * parent (guardian-role) user and a managed athlete linked to it.
 */
export default function ParentRegister() {
  const { registerFull } = useAuth();
  const navigate = useNavigate();
  const { exactMatch } = useSchoolData('player');

  // Parent fields
  const [pEmail, setPEmail] = useState('');
  const [pPassword, setPPassword] = useState('');
  const [pConfirm, setPConfirm] = useState('');
  const [pFirstName, setPFirstName] = useState('');
  const [pLastName, setPLastName] = useState('');
  const [pPhone, setPPhone] = useState('');

  // Athlete fields
  const [aFirstName, setAFirstName] = useState('');
  const [aLastName, setALastName] = useState('');
  const [aDob, setADob] = useState('');
  const [aEmail, setAEmail] = useState('');
  const [aSchool, setASchool] = useState('');
  const [aSchoolCity, setASchoolCity] = useState('');
  const [aSchoolState, setASchoolState] = useState('');
  const [aSchoolZip, setASchoolZip] = useState('');
  const [college, setCollege] = useState(EMPTY_COLLEGE);

  const [termsAccepted, setTermsAccepted] = useState(false);
  // Separate from termsAccepted and unchecked by default — see the matching
  // note in Register.jsx. The consent is the PARENT's, for the parent's own
  // mobile number; the child row it creates never gets sms_consent_at.
  const [smsConsent, setSmsConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [heardAbout, setHeardAbout] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const pwOk = useMemo(() => PW_RULES.every((r) => r.test(pPassword)), [pPassword]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!pEmail.trim() || !pPassword || !pFirstName.trim() || !pLastName.trim() || !pPhone.trim()) {
      setError('Please fill in all parent fields'); return;
    }
    if (pPassword !== pConfirm) { setError('Passwords do not match'); return; }
    if (!pwOk) { setError('Password does not meet the requirements'); return; }
    if (!isValidPhone(pPhone)) { setError('Enter a valid 10-digit US phone number'); return; }

    if (!aFirstName.trim() || !aLastName.trim() || !aDob) {
      setError('Athlete first name, last name, and date of birth are required'); return;
    }
    if (!termsAccepted) {
      setError('Please accept the Privacy Policy and Terms of Use to continue.'); return;
    }

    if (college.college && !collegeComplete(college)) {
      setError('Choose the athlete\'s college and years of eligibility'); return;
    }

    // Submit-time school enrichment, same pattern as the athlete flow.
    let resolvedCity = aSchoolCity;
    let resolvedState = aSchoolState;
    let resolvedZip = aSchoolZip;
    if ((!resolvedCity || !resolvedState) && aSchool.trim()) {
      const match = exactMatch(aSchool.trim());
      if (match) {
        if (!resolvedCity) resolvedCity = match.city || '';
        if (!resolvedState) resolvedState = match.state || '';
        if (!resolvedZip) resolvedZip = match.zip || '';
      }
    }

    setLoading(true);
    try {
      const result = await registerFull({
        email: pEmail.trim().toLowerCase(),
        password: pPassword,
        first_name: pFirstName.trim(),
        last_name: pLastName.trim(),
        phone: pPhone.trim(),
        terms_accepted: true,
        sms_consent: smsConsent,
        registration_type: 'parent_creates_athlete',
        // Athlete fields — backend expects these alongside the parent block.
        athlete_first_name: aFirstName.trim(),
        athlete_last_name: aLastName.trim(),
        athlete_date_of_birth: aDob,
        athlete_email: aEmail.trim() ? aEmail.trim().toLowerCase() : null,
        // School metadata is captured on the athlete; the register handler
        // copies these onto the AthleteProfile + the child User row.
        school: aSchool.trim() || null,
        school_city: resolvedCity || null,
        school_state: resolvedState || null,
        school_zip: resolvedZip || null,
        ...(collegePayload(college) || {}),
        acquisition: getAttribution() || undefined,
        heard_about: heardAbout || undefined,
      });
      // See Register.jsx: the record is now on the user row server-side, so
      // clearing it keeps a shared browser from attributing every later
      // signup to the first visitor's ad click. Only on success.
      clearAttribution();
      if (result?.unverified) {
        setSubmitted(true);
      } else {
        // Parent just registered an athlete — route to /upgrade so the
        // paywall is the next screen they see.
        navigate('/upgrade', { state: { from: 'register' } });
      }
    } catch (err) {
      setError(registrationErrorMessage(err));
    }
    setLoading(false);
  };

  const pwStarted = pPassword.length > 0;

  return (
    <div className="ed-auth">
      <div className="ed-auth__hero">
        <div>
          <MonoLabel onDark>JOIN PROZPECTS</MonoLabel>
          <h1 className="ed-auth__hero-headline" style={{ marginTop: 16 }}>
            Help your athlete<br /><em>get found.</em>
          </h1>
        </div>
        <p className="ed-auth__hero-quote">
          "We set up the athlete account; you manage it from your own login."
        </p>
      </div>

      <div className="ed-auth__form-wrap">
        <div className="ed-auth__form">
          <div className="auth-logo" style={{ marginBottom: 24 }}>
            <img src="/logo-wordmark.png" alt="ProZpects" style={{ maxHeight: 36 }} />
          </div>
          <MonoLabel>PARENT SIGN UP</MonoLabel>
          <h2 style={{
            fontFamily: 'var(--ed-font-serif)',
            fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em',
            margin: '8px 0 24px',
          }}>
            {submitted ? 'Check your email.' : 'Sign up.'}
          </h2>

          {submitted ? (
            <VerifySent email={pEmail} />
          ) : (
            <form className="auth-form" onSubmit={onSubmit}>
              {error && <div className="auth-error" role="alert">{error}</div>}

              <SectionHeader text="Your account" />

              <div className="auth-name-row">
                <div className="auth-field">
                  <label htmlFor="pr-first">First name</label>
                  <input id="pr-first" type="text" value={pFirstName}
                    onChange={(e) => setPFirstName(e.target.value)} required disabled={loading}
                    autoComplete="given-name" />
                </div>
                <div className="auth-field">
                  <label htmlFor="pr-last">Last name</label>
                  <input id="pr-last" type="text" value={pLastName}
                    onChange={(e) => setPLastName(e.target.value)} required disabled={loading}
                    autoComplete="family-name" />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="pr-email">Email</label>
                <input id="pr-email" type="email" value={pEmail}
                  onChange={(e) => setPEmail(e.target.value)} required disabled={loading}
                  autoComplete="email" />
              </div>

              <div className="auth-field">
                <label htmlFor="pr-phone">Phone</label>
                <input id="pr-phone" type="tel" value={pPhone}
                  onChange={(e) => setPPhone(formatPhone(e.target.value))} required disabled={loading}
                  autoComplete="tel-national" inputMode="tel" placeholder="(555) 555-5555" />
              </div>

              <div className="auth-field">
                <label htmlFor="pr-password">Password</label>
                <div className="auth-password-wrapper">
                  <input
                    id="pr-password"
                    type={showPassword ? 'text' : 'password'}
                    value={pPassword}
                    onChange={(e) => setPPassword(e.target.value)}
                    required disabled={loading}
                    autoComplete="new-password"
                  />
                  <Button type="button" variant="ghost" size="sm" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </div>
                <div className="pw-requirements" aria-live="polite">
                  {PW_RULES.map(({ key, label, test }) => {
                    const met = test(pPassword);
                    return (
                      <div key={key} className={`pw-req${pwStarted ? (met ? ' pw-req--met' : ' pw-req--unmet') : ''}`}>
                        <span className="pw-req__icon">{met ? '✓' : '○'}</span>{label}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="pr-confirm">Confirm password</label>
                <input id="pr-confirm" type={showPassword ? 'text' : 'password'} value={pConfirm}
                  onChange={(e) => setPConfirm(e.target.value)} required disabled={loading}
                  autoComplete="new-password" />
              </div>

              <SectionHeader text="Your athlete" sub="We'll create the athlete account linked to yours. You can promote them to manage their own login later." />

              <div className="auth-name-row">
                <div className="auth-field">
                  <label htmlFor="pr-a-first">Athlete first name</label>
                  <input id="pr-a-first" type="text" value={aFirstName}
                    onChange={(e) => setAFirstName(e.target.value)} required disabled={loading} />
                </div>
                <div className="auth-field">
                  <label htmlFor="pr-a-last">Athlete last name</label>
                  <input id="pr-a-last" type="text" value={aLastName}
                    onChange={(e) => setALastName(e.target.value)} required disabled={loading} />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="pr-a-dob">Athlete date of birth</label>
                <input id="pr-a-dob" type="date" value={aDob}
                  onChange={(e) => setADob(e.target.value)} required disabled={loading}
                  max={new Date().toISOString().slice(0, 10)} />
              </div>

              <div className="auth-field">
                <label htmlFor="pr-a-email">Athlete email <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
                <input id="pr-a-email" type="email" value={aEmail}
                  onChange={(e) => setAEmail(e.target.value)} disabled={loading} />
                <small style={{ color: 'var(--ed-color-text-secondary, #555)' }}>
                  Leave blank if your athlete doesn't have their own email yet — we'll auto-generate a placeholder.
                </small>
              </div>

              <SignupSchoolSection
                value={college}
                onChange={setCollege}
                onSwitchToCollege={() => { setASchool(''); setASchoolCity(''); setASchoolState(''); setASchoolZip(''); }}
                disabled={loading}
                who="athlete"
              >
                <label>Athlete's high school</label>
                <SchoolPicker
                  value={aSchool}
                  city={aSchoolCity}
                  state={aSchoolState}
                  zip={aSchoolZip}
                  onChange={({ name, city, state, zip }) => {
                    setASchool(name);
                    setASchoolCity(city);
                    setASchoolState(state);
                    setASchoolZip(zip);
                  }}
                  role="player"
                  disabled={loading}
                />
              </SignupSchoolSection>

              <div className="auth-field">
                <label htmlFor="pr-heard-about">
                  How did you hear about us? <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>
                </label>
                <select
                  id="pr-heard-about"
                  value={heardAbout}
                  onChange={(e) => setHeardAbout(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Prefer not to say</option>
                  {HEARD_ABOUT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <label className="auth-terms" style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                fontSize: 14, lineHeight: 1.4, marginTop: 4,
                color: 'var(--ed-color-text-secondary, #555)',
              }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={loading}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</Link>.
                </span>
              </label>

              {/* Standalone SMS opt-in — optional, unchecked by default, and NOT
                  part of the submit button's disabled condition. The disclosure
                  wording is what toll-free carrier vetting requires to be visible
                  in the opt-in workflow; do not trim it. */}
              <label className="auth-sms-consent" htmlFor="parent-sms-consent" style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                fontSize: 12.5, lineHeight: 1.45, marginTop: 8,
                padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--ed-color-border, #d7dde5)',
                color: 'var(--ed-color-text-secondary, #555)',
              }}>
                <input
                  id="parent-sms-consent"
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  disabled={loading}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong style={{ display: 'block', marginBottom: 2, fontSize: 13 }}>
                    Text me updates about my ProZpects account (optional)
                  </strong>
                  I agree to receive SMS text messages from ProZpects about my account and my
                  athlete's recruiting activity at the mobile number I entered above. Consent is
                  not a condition of creating an account or of any purchase. Message frequency
                  may vary. Standard message and data rates may apply. Reply STOP to opt out.
                  Reply HELP for assistance. Your mobile information will not be sold or shared
                  with third parties for promotional or marketing purposes. See our{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</Link>.
                </span>
              </label>

              <Button type="submit" variant="primary" className="auth-btn" disabled={loading || !termsAccepted}>
                {loading ? 'Creating accounts…' : 'Create accounts'}
              </Button>
            </form>
          )}

          <div className="auth-footer" style={{ marginTop: 32 }}>
            Already have an account? <Link to="/login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ text, sub }) {
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      <MonoLabel>{text.toUpperCase()}</MonoLabel>
      {sub && (
        <p style={{
          fontSize: 13,
          color: 'var(--ed-color-text-secondary, #555)',
          margin: '4px 0 0',
          lineHeight: 1.4,
        }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function VerifySent({ email }) {
  const { resendVerificationEmail } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  return (
    <div className="register-verify-pending">
      <p style={{ lineHeight: 1.5 }}>
        We sent a verification link to <strong>{email}</strong>. Tap it to finish setting up your account.
      </p>
      <Button
        type="button"
        variant="secondary"
        className="auth-btn auth-btn--secondary"
        disabled={sending || sent}
        onClick={async () => {
          setSending(true);
          try {
            await resendVerificationEmail?.(email);
            setSent(true);
          } catch {
            // ignore
          }
          setSending(false);
        }}
      >
        {sent ? 'Email sent — check your inbox' : sending ? 'Sending…' : 'Resend email'}
      </Button>
    </div>
  );
}
